import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';
import { ROUTES } from '@/common/auth-routes';
import { getTenantFromPath } from '@/common/tenant-utils';
import { getPosthogApiHost, getPosthogApiKey } from '@/config/env-config';
import { PAIR_ROUTE_SEGMENT } from '@/utils/pairing-utils';
import { scrubTelemetryUrl } from './scrub-url';

const TENANT_PLACEHOLDER = ':tenant';
const TOKEN_PLACEHOLDER = ':token';

const SECRET_PATH_SEGMENTS = [
  ROUTES.ONBOARDING.slice(1),
  ROUTES.TENANT_ONBOARDING.slice(1),
  ROUTES.ENROLLMENT_INVITATION.slice(1),
  PAIR_ROUTE_SEGMENT,
];

// Magic-link and pairing URLs carry their secret as a path segment
const SECRET_PATH_TOKEN = new RegExp(
  `/(${SECRET_PATH_SEGMENTS.join('|')})/[^/?#]+`,
  'g'
);

// Tenant slugs live only in the group key, never in URL properties
export function anonymizeTenantInPath(
  value: string,
  slug: string | null
): string {
  if (!slug) {
    return value;
  }

  return value
    .replace(`/${slug}/`, `/${TENANT_PLACEHOLDER}/`)
    .replace(new RegExp(`/${slug}([?#]|$)`), `/${TENANT_PLACEHOLDER}$1`);
}

export function scrubEventUrls(event: CaptureResult | null) {
  if (!event) {
    return null;
  }

  // before_send runs after pushState, so the current path is the event's
  const slug = getTenantFromPath();

  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (!bag) {
      continue;
    }

    for (const [key, value] of Object.entries(bag)) {
      if (typeof value === 'string') {
        const scrubbed = scrubTelemetryUrl(value).replace(
          SECRET_PATH_TOKEN,
          `/$1/${TOKEN_PLACEHOLDER}`
        );
        bag[key] = anonymizeTenantInPath(scrubbed, slug);
      }
    }
  }

  if (typeof event.properties?.$pathname === 'string') {
    event.properties.route = event.properties.$pathname;
  }

  return event;
}

let initialized = false;

export function initializePosthog(): void {
  const apiKey = getPosthogApiKey();

  if (!apiKey) {
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: getPosthogApiHost(),
      ui_host: 'https://us.posthog.com',
      defaults: '2026-06-25',
      // No DOM serialization: page text and form values never leave the browser
      autocapture: false,
      person_profiles: 'identified_only',
      // Replay stays off even if enabled on the project (SPRK-6008)
      disable_session_recording: true,
      // CSP blocks the lazily loaded toolbar
      disable_external_dependency_loading: true,
      disable_surveys: true,
      // /flags posts raw $initial_* URLs, bypassing before_send
      advanced_disable_flags: true,
      before_send: scrubEventUrls,
    });
    posthog.register({ app: 'user-portal' });
    initialized = true;
  } catch (error) {
    console.error('[PostHog] Failed to initialize:', error);
  }
}

export function resetPosthogUser(): void {
  if (!initialized) {
    return;
  }

  try {
    posthog.reset();
  } catch (error) {
    console.error('[PostHog] Failed to reset identity:', error);
  }
}

interface PosthogUser {
  id: string;
  tenantSlug?: string;
}

// Pseudonymous: internal ids only, never email or name (SPRK-6008)
export function identifyPosthogUser(user: PosthogUser): void {
  if (!initialized) {
    return;
  }

  try {
    posthog.identify(user.id);

    if (user.tenantSlug) {
      posthog.group('tenant', user.tenantSlug);
    }
  } catch (error) {
    console.error('[PostHog] Failed to identify user:', error);
  }
}
